import React, { useState, useRef, useId, useEffect } from 'react';
import { motion } from 'motion/react';
import { X, Settings, LogOut, Crown, User as UserIcon, Upload, Plus, Search, HelpCircle, BookOpen, FileText, StickyNote, ChevronRight, Loader2, Trash2 } from 'lucide-react';
import { useAuth, useUser, useClerk } from '@clerk/clerk-react';
import SourcityLogo from '../../imports/SourcityLogoWordmarkIconEmbedded1';
import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';
import { useLibraryStore } from '../../stores/libraryStore';
import { useUserStore } from '../../stores/userStore';
import { useNotesStore } from '../../stores/notesStore';
import { useQuizStore } from '../../stores/quizStore';
import { toast } from 'sonner';
import { LanguageOptions } from './language-toggle';
import { useI18n } from '../../i18n';
import { useSwipeClickGuard } from './use-swipe-click-guard';

interface MobileMenuDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onShowProfile: () => void;
  onShowChallenges: (createNew?: boolean) => void;
  onShowSettings?: () => void;
  onShowHelp?: () => void;
}

export function MobileMenuDrawer({ isOpen, onClose, onShowProfile, onShowChallenges, onShowSettings, onShowHelp }: MobileMenuDrawerProps) {
  const { t, isRTL } = useI18n();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSection, setExpandedSection] = useState<'library' | 'challenge' | 'notes' | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [swipedItem, setSwipedItem] = useState<string | null>(null);
  const deletePending = useRef(false);
  const materialLabelPrefix = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSwipedItem(null);
  }, [isOpen, expandedSection, isRTL]);
  
  // Auth hooks
  const { getToken } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  
  // Store hooks
  const {
    materials,
    selectMaterial,
    uploadFile,
    pollUploadStatus,
    deleteMaterial,
    isUploading: storeUploading,
  } = useLibraryStore();
  const { profile } = useUserStore();
  const { notes } = useNotesStore();
  const { savedChallenges } = useQuizStore();
  
  // Get user display info
  const userName = user?.firstName || user?.username || profile?.display_name || t('profile.defaultUser');
  const userTier = profile?.subscription_tier || 'free';
  const isPro = userTier === 'pro' || userTier === 'paid' || userTier === 'premium';
  
  // Safe arrays
  const safeeMaterials = Array.isArray(materials) ? materials : [];
  const safeNotes = Array.isArray(notes) ? notes : [];
  
  // Filter notes by search
  const filteredNotes = safeNotes.filter(note => 
    note.note_content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    note.selected_text?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const toggleSection = (section: 'library' | 'challenge' | 'notes') => {
    setExpandedSection(expandedSection === section ? null : section);
  };
  
  // Handle file upload
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setIsUploading(true);
      const token = await getToken();
      if (!token) {
        toast.error(t('library.toast.signInToUpload'));
        return;
      }
      const sessionId = await uploadFile(file, token);
      toast.info(t('library.toast.queued'));
      onClose();
      const material = await pollUploadStatus(sessionId, getToken);
      if (material) selectMaterial(material);
      toast.success(t('library.toast.uploaded'));
    } catch (error) {
      toast.error(t('library.toast.uploadFailed', { error: (error as Error).message }));
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  
  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut();
      onClose();
    } catch (error) {
      console.error('Logout error:', error);
      toast.error(t('profile.toast.logoutFailed'));
    }
  };
  
  // Handle material selection
  const handleSelectMaterial = (material: typeof safeeMaterials[0]) => {
    selectMaterial(material);
    toast.info(t('library.toast.opening', { name: material.original_filename || material.filename }));
    onClose();
  };

  const handleDeleteMaterial = async (material: typeof safeeMaterials[0]) => {
    // Lock before confirmation/token refresh: repeated taps can arrive before
    // React has rendered the disabled buttons.
    if (deletePending.current) return;
    deletePending.current = true;
    try {
      if (!confirm(t('library.confirmDeleteNamed', { name: material.original_filename || material.filename }))) {
        setSwipedItem(null);
        return;
      }
      setDeletingId(material.id);
      const token = await getToken();
      if (!token) {
        toast.error(t('library.toast.signIn'));
        return;
      }
      await deleteMaterial(material.id, token);
      toast.success(t('library.toast.deleted'));
      setSwipedItem(null);
    } catch (error) {
      toast.error(t('library.toast.deleteFailed'));
      console.error('Delete error:', error);
    } finally {
      deletePending.current = false;
      setDeletingId(null);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div
        className={`fixed top-0 end-0 bottom-0 w-[320px] z-50 md:hidden transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full rtl:-translate-x-full'
        }`}
      >
        <div className="flex h-full min-h-0 flex-col bg-white/90 backdrop-blur-2xl border-s border-white/40 shadow-2xl">
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between p-4 border-b border-white/40">
            <div className="w-28 h-7">
              <SourcityLogo />
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/60 hover:bg-white/80 active:scale-95 transition-all"
            >
              <X className="h-5 w-5 text-[#434343]" />
            </button>
          </div>
          
          {/* User Info */}
          <div className="shrink-0 p-4 border-b border-white/40">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-white/60">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#7367FF] to-[#2DD2FE] flex items-center justify-center overflow-hidden">
                {user?.imageUrl ? (
                  <img src={user.imageUrl} alt={userName} className="w-full h-full object-cover" />
                ) : (
                <UserIcon className="h-6 w-6 text-white" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-[#434343]">{userName}</p>
                <div className="flex items-center gap-1 mt-0.5">
                  {isPro ? (
                    <>
                  <Crown className="h-3 w-3 text-[#7367FF]" />
                  <span className="text-xs text-[#7367FF]">{t('profile.proMember')}</span>
                    </>
                  ) : (
                    <span className="text-xs text-[#434343]/60">{t('common.freePlan')}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Scrollable Content Area — min-h-0 so flex-1 can shrink and scroll */}
          <ScrollArea className="min-h-0 flex-1">
            <div className="p-4 space-y-2">
              <LanguageOptions className="mb-2" />

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                onChange={handleFileChange}
                className="hidden"
              />
              
              {/* Library Section */}
              <div className="space-y-2">
                <button
                  onClick={() => toggleSection('library')}
                  className="flex w-full items-center justify-between gap-3 p-3 rounded-xl bg-white/60 hover:bg-white/80 active:scale-[0.98] transition-all"
                >
                  <div className="flex items-center gap-3">
                    <BookOpen className="h-5 w-5 text-[#7367FF]" />
                    <span className="text-sm font-medium text-[#434343]">{t('nav.library')}</span>
                    {safeeMaterials.length > 0 && (
                      <span className="text-xs text-[#434343]/60">({safeeMaterials.length})</span>
                    )}
                  </div>
                  <ChevronRight className={`h-4 w-4 text-[#434343] transition-transform ${expandedSection === 'library' ? 'rotate-90' : 'rtl:rotate-180'}`} />
                </button>
                
                {expandedSection === 'library' && (
                  <div className="ps-4 space-y-1.5">
                    <button 
                      onClick={handleUploadClick}
                      disabled={isUploading || storeUploading}
                      className="flex w-full items-center gap-2 p-2.5 rounded-lg text-start text-[#7367FF] bg-white/40 hover:bg-white/60 active:scale-[0.98] transition-all disabled:opacity-50"
                    >
                      {(isUploading || storeUploading) ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                      <Upload className="h-4 w-4" />
                      )}
                      <span className="text-sm">{(isUploading || storeUploading) ? t('common.uploading') : t('library.upload')}</span>
                    </button>
                    {safeeMaterials.length === 0 ? (
                      <p className="text-xs text-[#434343]/50 px-2.5 py-2">{t('library.empty')}</p>
                    ) : (
                      safeeMaterials.map((item) => (
                      <MobileLibrarySwipeRow
                          key={item.id}
                          label={item.original_filename || item.filename}
                          labelId={`${materialLabelPrefix}-${item.id}`}
                          isOpen={swipedItem === item.id}
                          isDeleting={deletingId === item.id}
                          disabled={deletingId !== null}
                          onReveal={(open) => setSwipedItem(open ? item.id : null)}
                          onSelect={() => handleSelectMaterial(item)}
                          onDelete={() => void handleDeleteMaterial(item)}
                      />
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Challenge Section */}
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => toggleSection('challenge')}
                  aria-expanded={expandedSection === 'challenge'}
                  aria-controls="mobile-challenge-section"
                  className="flex w-full items-center justify-between gap-3 p-3 rounded-xl bg-white/60 hover:bg-white/80 active:scale-[0.98] transition-all"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-[#7367FF]" />
                    <span className="text-sm font-medium text-[#434343]">{t('nav.challenge')}</span>
                  </div>
                  <ChevronRight className={`h-4 w-4 text-[#434343] transition-transform ${expandedSection === 'challenge' ? 'rotate-90' : 'rtl:rotate-180'}`} />
                </button>
                
                {expandedSection === 'challenge' && (
                  <div id="mobile-challenge-section" className="ps-4 space-y-1.5">
                    <button 
                      type="button"
                      onClick={() => { onClose(); onShowChallenges(true); }}
                      className="flex min-h-11 w-full items-center gap-2 p-2.5 rounded-lg text-start text-[#7367FF] bg-white/40 hover:bg-white/60 active:scale-[0.98] transition-all"
                    >
                      <Plus className="h-4 w-4" />
                      <span className="text-sm">{t('challenge.createNew')}</span>
                    </button>
                    {savedChallenges.length > 0 ? (
                      <button type="button" onClick={() => { onClose(); onShowChallenges(false); }}
                        className="flex min-h-11 w-full items-center justify-between gap-2 rounded-lg bg-white/40 p-2.5 text-start text-sm text-[#434343] hover:bg-white/60">
                        <span>{t('challenge.title')}</span>
                        <span>{savedChallenges.length}</span>
                      </button>
                    ) : (
                      <p className="text-xs text-[#434343]/50 px-2.5 py-2">{t('challenge.empty')}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Notes Section */}
              <div className="space-y-2">
                <button
                  onClick={() => toggleSection('notes')}
                  className="flex w-full items-center justify-between gap-3 p-3 rounded-xl bg-white/60 hover:bg-white/80 active:scale-[0.98] transition-all"
                >
                  <div className="flex items-center gap-3">
                    <StickyNote className="h-5 w-5 text-[#7367FF]" />
                    <span className="text-sm font-medium text-[#434343]">{t('nav.notes')}</span>
                    {safeNotes.length > 0 && (
                      <span className="text-xs text-[#434343]/60">({safeNotes.length})</span>
                    )}
                  </div>
                  <ChevronRight className={`h-4 w-4 text-[#434343] transition-transform ${expandedSection === 'notes' ? 'rotate-90' : 'rtl:rotate-180'}`} />
                </button>
                
                {expandedSection === 'notes' && (
                  <div className="ps-4 space-y-2">
                    {/* Search Box */}
                    <div className="relative">
                      <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#434343]" />
                      <Input 
                        placeholder={t('notes.searchPlaceholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="ps-9 bg-white/60 backdrop-blur-sm border-white/40 h-9 text-sm"
                      />
                    </div>
                    
                    {/* Notes List */}
                    <div className="space-y-1.5">
                      {filteredNotes.length === 0 ? (
                        <p className="text-xs text-[#434343]/50 px-2.5 py-2">
                          {searchQuery ? t('notes.noSearchMatch') : t('notes.emptyShort')}
                        </p>
                      ) : (
                        filteredNotes.slice(0, 10).map((note) => (
                        <button 
                            key={note.id}
                            onClick={() => {
                              toast.info(t('notes.toast.selected'));
                              onClose();
                            }}
                            className="flex w-full flex-col items-start p-2.5 rounded-lg text-start bg-white/40 hover:bg-white/60 active:scale-[0.98] transition-all"
                        >
                            <span className="text-sm text-[#434343] line-clamp-2">
                              {note.selected_text || note.note_content?.slice(0, 50) + '...'}
                            </span>
                            {note.document_title && (
                              <span className="text-xs text-[#434343]/50 mt-0.5">
                                {t('notes.fromDocument', { title: note.document_title })}
                              </span>
                            )}
                        </button>
                        ))
                      )}
                      {filteredNotes.length > 10 && (
                        <p className="text-xs text-[#7367FF] px-2.5">
                          {t('notes.moreNotes', { count: filteredNotes.length - 10 })}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="py-2">
                <div className="border-t border-white/40" />
              </div>

              {/* Other Menu Items */}
              <button
                onClick={() => {
                  onShowProfile();
                  onClose();
                }}
                className="flex w-full items-center gap-3 p-3 rounded-xl bg-white/60 hover:bg-white/80 active:scale-[0.98] transition-all"
              >
                <UserIcon className="h-5 w-5 text-[#7367FF]" />
                <span className="text-sm font-medium text-[#434343]">{t('common.profile')}</span>
              </button>
              
              <button
                onClick={() => {
                  onShowSettings?.();
                  onClose();
                }}
                className="flex w-full items-center gap-3 p-3 rounded-xl bg-white/60 hover:bg-white/80 active:scale-[0.98] transition-all"
              >
                <Settings className="h-5 w-5 text-[#7367FF]" />
                <span className="text-sm font-medium text-[#434343]">{t('common.settings')}</span>
              </button>
              
              <button
                onClick={() => {
                  onShowHelp?.();
                  onClose();
                }}
                className="flex w-full items-center gap-3 p-3 rounded-xl bg-white/60 hover:bg-white/80 active:scale-[0.98] transition-all"
              >
                <HelpCircle className="h-5 w-5 text-[#7367FF]" />
                <span className="text-sm font-medium text-[#434343]">{t('common.helpAndSupport')}</span>
              </button>
            </div>
          </ScrollArea>
          
          {/* Logout - Fixed at bottom */}
          <div className="shrink-0 border-t border-white/40 bg-white/90 p-4">
            <button 
              onClick={handleLogout}
              className="flex w-full items-center justify-center gap-3 p-3 rounded-xl bg-white/60 hover:bg-red-50 active:scale-[0.98] transition-all"
            >
              <LogOut className="h-5 w-5 text-red-600" />
              <span className="text-sm font-medium text-red-600">{t('common.logOut')}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function MobileLibrarySwipeRow({ label, labelId, isOpen, isDeleting, disabled, onReveal, onSelect, onDelete }: {
  label: string;
  labelId: string;
  isOpen: boolean;
  isDeleting: boolean;
  disabled: boolean;
  onReveal: (open: boolean) => void;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const { t, isRTL } = useI18n();
  const swipeClickGuard = useSwipeClickGuard();
  const dragOrigin = useRef(0);
  const foregroundRef = useRef<HTMLButtonElement>(null);
  const revealOffset = isRTL ? 74 : -74;
  const actionId = `${labelId}-delete`;

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} aria-busy={isDeleting} className="relative w-full min-w-0 overflow-hidden rounded-lg bg-white"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && isOpen) {
          event.preventDefault();
          onReveal(false);
          foregroundRef.current?.focus();
        }
      }}
    >
      <motion.button
        {...swipeClickGuard}
        ref={foregroundRef}
        type="button"
        disabled={disabled}
        aria-expanded={isOpen}
        aria-controls={actionId}
        aria-keyshortcuts={`${isRTL ? 'ArrowRight' : 'ArrowLeft'} Escape`}
        drag={disabled ? false : 'x'}
        dragConstraints={isRTL ? { left: 0, right: 74 } : { left: -74, right: 0 }}
        dragElastic={0}
        dragMomentum={false}
        onDragStart={() => {
          swipeClickGuard.onDragStart();
          dragOrigin.current = isOpen ? revealOffset : 0;
        }}
        onDragEnd={(_event, info) => {
          if (!disabled) {
            const offset = dragOrigin.current + info.offset.x;
            onReveal(isRTL ? offset > 25 : offset < -25);
          }
        }}
        animate={{ x: isOpen ? revealOffset : 0 }}
        transition={{ type: 'spring', stiffness: 500, damping: 40 }}
        onKeyDown={(event) => {
          if (event.key === (isRTL ? 'ArrowRight' : 'ArrowLeft')) {
            event.preventDefault();
            onReveal(true);
          }
        }}
        onClick={() => isOpen ? onReveal(false) : onSelect()}
        className="relative z-10 flex min-h-11 w-full min-w-0 items-center rounded-lg bg-white p-2.5 text-start hover:bg-gray-50"
        style={{ touchAction: 'pan-y', userSelect: 'none', WebkitUserSelect: 'none' }}
      >
        <span id={labelId} dir="auto" className="truncate text-sm text-[#434343]">{label}</span>
      </motion.button>
      <button
        id={actionId}
        type="button"
        aria-label={t('common.delete')}
        aria-describedby={labelId}
        aria-hidden={!isOpen}
        style={{ visibility: isOpen ? 'visible' : 'hidden' }}
        tabIndex={isOpen ? 0 : -1}
        disabled={disabled || !isOpen}
        onClick={onDelete}
        className={`absolute end-0 top-0 bottom-0 flex w-[74px] items-center justify-center bg-[#ff3b30] text-white active:bg-[#e53429] disabled:opacity-50 ${isOpen ? '' : 'pointer-events-none'}`}
      >
        {isDeleting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Trash2 className="h-5 w-5" />}
      </button>
    </div>
  );
}
